export interface LocationPopulateInput {
  /** true when the lane lock applies (camera / Pusher LPR with lane_id) */
  isLaneLocked: boolean;
  /** true when the location on the form is one the user explicitly picked */
  userSelectedLocation: boolean;
  existing: { locationId?: string; location?: string };
  notification: { locationId?: string; branch?: string };
}

export interface ResolvedFormLocation {
  locationId: string;
  location: string;
}

/**
 * Decides which location the order form shows after a notification is (re)applied.
 * Lane-locked popups always follow the lane. Otherwise the location on the form survives
 * re-emissions (plate upload, SSE images-ready, Axis entry-updated) when the user picked it, or when
 * the notification names none; a mere default gives way to the notification's location.
 */
export function resolveFormLocationOnPopulate(input: LocationPopulateInput): ResolvedFormLocation {
  // API ids can arrive as numbers; the form and its callers treat them as strings.
  const existingId = String(input.existing.locationId || '');
  const notificationId = String(input.notification.locationId || '');
  const keepExisting = input.userSelectedLocation || !notificationId;
  if (!input.isLaneLocked && keepExisting && existingId) {
    return { locationId: existingId, location: input.existing.location || '' };
  }
  return { locationId: notificationId, location: input.notification.branch || '' };
}

export interface LocationHeaderInput {
  /** Header "+" order: no scan context, the operator picks everything. */
  isDirectOrder: boolean;
  /** UI plate image upload (`is_lpr_upload`): location stays editable. */
  isUiUpload: boolean;
  /** Camera / Pusher LPR with a lane: the lane pins the location. */
  isLaneTriggered: boolean;
  /** Number of locations the operator can choose from (0 until the list has loaded, or if it failed). */
  locationCount: number;
  isShopFloor: boolean;
}

/**
 * The location picker only earns its space for an operator choosing by hand. It is hidden once the
 * operator is known to have a single location; an empty list (loading or failed) keeps it visible so
 * its dropdown can show the loading state and retry.
 */
export function computeLocationHeaderVisible(input: LocationHeaderInput): boolean {
  return (
    !input.isShopFloor &&
    !input.isLaneTriggered &&
    input.locationCount !== 1 &&
    (input.isDirectOrder || input.isUiUpload)
  );
}

/** Why the location picker was on the order form; carried across the payment step so Back restores it. */
export type LocationPickerOrigin = 'direct' | 'ui-upload';

export const LOCATION_PICKER_ORIGIN_KEY = 'location_picker_origin';

/**
 * The lane the attendant picked on a UI plate upload, carried across the payment step. Kept apart
 * from `lane_id`, which `resolveNotificationLaneId` reads as a camera lane.
 */
export const LOCATION_PICKER_LANE_KEY = 'location_picker_lane_id';

/** Fields stamped onto the checkout data so Back restores the picker origin and, for an upload, its lane. */
export function buildLocationPickerStamp(
  origin: LocationPickerOrigin | null,
  laneId: string | null | undefined
): Record<string, string> {
  const stamp: Record<string, string> = {};
  if (origin) {
    stamp[LOCATION_PICKER_ORIGIN_KEY] = origin;
  }
  const lane = String(laneId ?? '').trim();
  if (origin === 'ui-upload' && lane) {
    stamp[LOCATION_PICKER_LANE_KEY] = lane;
  }
  return stamp;
}

/** Lane to restore into the order form on Back; only a UI-upload origin ever picks a lane. */
export function resolveCarriedPickedLaneId(
  origin: LocationPickerOrigin | null,
  data: Record<string, any> | null | undefined
): string {
  if (origin !== 'ui-upload') {
    return '';
  }
  const carried = data?.[LOCATION_PICKER_LANE_KEY];
  return typeof carried === 'string' || typeof carried === 'number' ? String(carried).trim() : '';
}

/**
 * The checkout notification (`create-payment-request`) drops the `quick_plus_order` / `is_lpr_upload`
 * flags, so Back → edit would hide a picker that was visible. Stamp the origin into the checkout data
 * and read it back here; an unset or unknown value means the picker was not shown.
 */
export function resolveLocationPickerOrigin(input: {
  isDirectOrder: boolean;
  isUiUpload: boolean;
  notificationData?: Record<string, any> | null;
}): LocationPickerOrigin | null {
  if (input.isDirectOrder) {
    return 'direct';
  }
  if (input.isUiUpload) {
    return 'ui-upload';
  }
  const carried = input.notificationData?.[LOCATION_PICKER_ORIGIN_KEY];
  return carried === 'direct' || carried === 'ui-upload' ? carried : null;
}

/**
 * The checkout notification is built from the POST /order response, so it loses what the order form
 * showed: the plate / vehicle capture and the scan lane. Copy those across so Back → edit looks as it
 * did when the order was created.
 */
export function pickOrderRequestContextForCheckout(
  data: Record<string, any> | null | undefined
): Record<string, any> {
  const carried: Record<string, any> = {};
  for (const key of ['plate_image_s3_url', 'image_s3_url'] as const) {
    const value = typeof data?.[key] === 'string' ? data[key].trim() : '';
    if (value) {
      carried[key] = value;
    }
  }
  const laneId = data?.['lane_id'];
  if (typeof laneId === 'number' || (typeof laneId === 'string' && laneId.trim())) {
    carried['lane_id'] = laneId;
  }
  return carried;
}

/**
 * Ids that tie a notification to one LPR entry. A replacement notification (SSE, list refresh,
 * rehydrate) keeps these while dropping flags such as `is_lpr_upload`, so they identify "the same popup".
 * `order_id` is only used when no entry id is present: an order id alone is not specific enough to
 * say two notifications are the same popup.
 */
export function collectNotificationIdentityKeys(
  data: Record<string, any> | null | undefined
): string[] {
  const read = (keys: string[]): string[] => {
    const found = new Set<string>();
    for (const key of keys) {
      const value = data?.[key];
      if (typeof value === 'string' || typeof value === 'number') {
        const id = String(value).trim();
        if (id) {
          found.add(id);
        }
      }
    }
    return [...found];
  };
  const entryKeys = read([
    'lpr_entry_id',
    'lprEntryId',
    'licence_plate_id',
    'license_plate_id',
    'id',
  ]);
  return entryKeys.length ? entryKeys : read(['order_id', 'orderId']);
}
